import React from 'react';
import { Link, useParams, Navigate, useLocation } from 'react-router-dom';
import { ArrowRight, Check, ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';
import { serviceCategories } from '../data/serviceData';

// List of valid service slugs for routing validation
const validServiceSlugs = [
  'abattage-arbre-quebec', 'elagage-quebec', 'demontage-arbres-quebec', 
  'taille-haies-quebec', 'essouchage-quebec',
  'amenagement-paysager-quebec', 'plantation-arbres-quebec', 'plantation-cedres-quebec',
  'plates-bandes-quebec', 'paillis-bordures-quebec', 'nivellement-terrain-quebec',
  'deboisement-quebec', 'debroussaillage-quebec', 'degagement-plantations-quebec',
  'preparation-terrain-forestier-quebec', 'travaux-boise-quebec', 'chemins-forestiers-quebec'
];

// Find service by slug across all categories
const findServiceBySlug = (slug) => {
  for (const categoryKey of Object.keys(serviceCategories)) {
    const category = serviceCategories[categoryKey];
    const service = category.services.find(s => s.slug === `/${slug}`);
    if (service) {
      return { service, category };
    }
  }
  return null;
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const location = useLocation();
  
  // First check if this is a valid service slug
  if (!validServiceSlugs.includes(slug)) {
    // If not a valid service slug, redirect to home
    return <Navigate to="/" replace />;
  }
  
  const result = findServiceBySlug(slug);

  // Redirect to abattage-elagage if service not found
  if (!result) {
    return <Navigate to="/abattage-elagage" replace />;
  }

  const { service, category } = result;

  return (
    <div className="page-service-detail">
      <SEO 
        title={`${service.title} à Québec | PaysFor`}
        description={service.description}
        keywords={`${service.title.toLowerCase()} Québec, ${service.title.toLowerCase()} Capitale-Nationale, PaysFor`}
        canonical={service.slug}
      />
      
      {/* Breadcrumb */}
      <section className="breadcrumb-section">
        <div className="container">
          <nav className="breadcrumb">
            <Link to="/">Accueil</Link>
            <span className="breadcrumb-separator">/</span>
            <Link to={category.slug}>{category.title}</Link>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">{service.title}</span>
          </nav>
        </div>
      </section>

      {/* Hero Section */}
      <section className="service-detail-hero">
        <div className="container">
          <div className="service-detail-hero-grid">
            <div className="service-detail-hero-content">
              <Link to={category.slug} className="back-link">
                <ArrowLeft size={18} />
                Retour à {category.title}
              </Link>
              <h1 className="display-medium">{service.title}</h1>
              <p className="body-large">{service.description}</p>
              
              <div className="service-benefits-hero">
                <h3 className="heading-3">Pourquoi choisir PaysFor?</h3>
                <ul className="benefits-list-hero">
                  {service.benefits.map((benefit, i) => (
                    <li key={i}>
                      <Check size={20} className="benefit-icon" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Link to="/contact" className="btn-cta">
                Demander une soumission gratuite
                <ArrowRight size={20} style={{ marginLeft: '8px' }} />
              </Link>
            </div>
            <div className="service-detail-hero-image">
              <img src={service.image} alt={service.title} />
            </div>
          </div>
        </div>
      </section>

      {/* Service Description Section */}
      <section className="service-description-section">
        <div className="container">
          <div className="service-description-content">
            <h2 className="heading-1">Service de {service.title.toLowerCase()} à Québec</h2>
            <p className="body-large">
              Chez PaysFor, nous offrons des services professionnels de {service.title.toLowerCase()} 
              dans toute la région de Québec et la Capitale-Nationale. Notre équipe d'experts 
              utilise des techniques éprouvées et de l'équipement spécialisé pour garantir 
              des résultats de qualité supérieure.
            </p>
            <p className="body-medium">
              Que vous soyez un particulier ou une entreprise, nous adaptons nos services 
              à vos besoins spécifiques. Contactez-nous pour une évaluation gratuite 
              et une soumission sans engagement.
            </p>
          </div>
        </div>
      </section>

      {/* Other Services in Category */}
      <section className="related-services-section">
        <div className="container">
          <h2 className="heading-1 section-title">Autres services de {category.title.toLowerCase()}</h2>
          <div className="related-services-grid">
            {category.services
              .filter(s => s.id !== service.id)
              .slice(0, 4)
              .map((relatedService) => (
                <Link 
                  key={relatedService.id} 
                  to={relatedService.slug}
                  className="related-service-card"
                >
                  <div className="related-service-image">
                    <img src={relatedService.image} alt={relatedService.title} loading="lazy" />
                  </div>
                  <div className="related-service-content">
                    <h3 className="heading-3">{relatedService.title}</h3>
                    <span className="related-service-link">
                      En savoir plus <ArrowRight size={16} />
                    </span>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-detail-cta">
        <div className="container">
          <div className="cta-content">
            <h2 className="heading-1">Besoin de {service.title.toLowerCase()}?</h2>
            <p className="body-large">
              Contactez-nous dès aujourd'hui pour discuter de votre projet et obtenir une soumission gratuite.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn-cta">
                Demander une soumission gratuite
                <ArrowRight size={20} style={{ marginLeft: '8px' }} />
              </Link>
              <a href="tel:+15147144245" className="btn-secondary">
                Appelez-nous maintenant
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
